import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Topbar, Footer, SEO } from '@components'
import { useStoreActions, useStoreState, EventStatus } from '@stores'
import { CLIENT_CODE, SITE_URL } from '@constants'

const formatEventDate = (start?: string, end?: string): string => {
	if (!start && !end) return ''
	const fmt = (s?: string) => {
		if (!s) return ''
		const d = new Date(s)
		if (isNaN(d.getTime())) return s
		return d.toLocaleDateString('en-GB', {
			day: 'numeric',
			month: 'short',
			year: 'numeric'
		})
	}
	if (start && end) return `${fmt(start)} – ${fmt(end)}`
	return fmt(start || end)
}

export const Event = () => {
	const [activeFilter, setActiveFilter] = useState<EventStatus>('all')

	const filters: { id: EventStatus; label: string }[] = [
		{ id: 'all', label: 'All Events' },
		{ id: 'running', label: 'Ongoing' },
		{ id: 'past', label: 'Past' }
	]

	const { fetchAllEvent } = useStoreActions(action => action.event)
	const { events } = useStoreState(state => state.event)

	useEffect(() => {
		fetchAllEvent({
			page: 1,
			limit: 100,
			client_code: CLIENT_CODE,
			status: activeFilter
		})
	}, [fetchAllEvent, activeFilter])

	const eventListJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: 'Events — Botani Square Mall',
		url: `${SITE_URL}/event`,
		numberOfItems: (events || []).length,
		itemListElement: (events || []).slice(0, 50).map((e, i) => ({
			'@type': 'ListItem',
			position: i + 1,
			url: `${SITE_URL}/event/${e.slug}`,
			name: e.name
		}))
	}

	const breadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Events',
				item: `${SITE_URL}/event`
			}
		]
	}

	return (
		<Fragment>
			<SEO
				title="Events & Activities"
				description="Upcoming and ongoing events at Botani Square Mall Bogor — family activities, festivals, exhibitions, and more."
				path="/event"
				keywords={[
					'event Botani Square',
					'event Bogor',
					'agenda mall Bogor',
					'kids event Bogor'
				]}
				jsonLd={[eventListJsonLd, breadcrumb]}
			/>
			<Topbar />

			{/* Hero Section with Banner Image */}
			<section className="relative overflow-hidden pt-28 pb-10 sm:pt-32 sm:pb-14">
				{/* Background Banner Image */}
				<div className="absolute inset-0">
					<img
						src={require('@assets/images/banner-2.png')}
						alt="Event Banner"
						className="w-full h-full object-cover"
					/>
					{/* Overlay for better text readability */}
					<div className="absolute inset-0 bg-primary bg-opacity-60"></div>
				</div>

				{/* Content */}
				<div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
					<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
						Upcoming Events
					</h1>
					<p className="text-sm sm:text-base text-gray-100 max-w-2xl mx-auto">
						Join us for exciting events, workshops, and entertainment throughout
						the year.
					</p>
				</div>
			</section>

			{/* Event Filter and Count Section */}
			<section className="py-12 bg-white">
				<div className="max-w-screen-xl mx-auto px-6">
					<div className="flex flex-col md:flex-row justify-between items-center">
						{/* Event Count */}
						<div className="flex items-center mb-6 md:mb-0">
							<div className="w-6 h-6 bg-primary rounded mr-3 flex items-center justify-center">
								<svg
									className="w-4 h-4 text-white"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
										clipRule="evenodd"
									/>
								</svg>
							</div>
							<span className="text-gray-700 font-medium">
								{events && events.length > 0
									? `${events.length} ${events.length > 1 ? 'Events' : 'Event'}`
									: 'No Events Available'}
							</span>
						</div>

						{/* Filter Buttons */}
						<div className="flex flex-wrap gap-3">
							{filters.map(filter => (
								<button
									key={filter.id}
									onClick={() => setActiveFilter(filter.id)}
									className={`px-6 py-3 rounded-lg font-medium transition-colors ${
										activeFilter === filter.id
											? 'bg-primary text-white'
											: 'bg-white text-gray-700 border border-gray-200 hover:border-primary hover:text-primary'
									}`}
								>
									{filter.label}
								</button>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* Event Listings Section */}
			<section className="py-12 sm:py-16 md:py-20 bg-gray-50">
				<div className="max-w-screen-xl mx-auto px-4 sm:px-6">
					{events && events.length > 0 ? (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
							{events.map((event, index) => {
								const dateLabel = formatEventDate(
									event.period?.start,
									event.period?.end
								)
								return (
									<Link
										key={event.id || index}
										to={`/event/${event.slug}`}
										className="group flex flex-col bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
									>
										<div className="relative aspect-square bg-gradient-to-br from-blue-100 to-indigo-100 overflow-hidden">
											{event.photos && event.photos.length > 0 ? (
												<img
													src={event.photos[0]}
													alt={event.name || 'Event Image'}
													className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
												/>
											) : (
												<div className="absolute inset-0 flex items-center justify-center text-4xl">
													🎉
												</div>
											)}
										</div>
										<div className="flex flex-col flex-1 p-5 sm:p-6">
											{dateLabel && (
												<div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
													{dateLabel}
												</div>
											)}
											<h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 line-clamp-2 flex-1">
												{event.name || 'Event Title'}
											</h3>
											<span className="inline-flex items-center gap-1 text-primary font-medium text-sm mt-auto group-hover:underline">
												View details
												<span aria-hidden>→</span>
											</span>
										</div>
									</Link>
								)
							})}
						</div>
					) : (
						<div className="text-center py-16">
							<div className="text-6xl mb-4">🎉</div>
							<h3 className="text-2xl font-bold text-gray-800 mb-4">
								No Events Available
							</h3>
							<p className="text-gray-600 text-lg">
								Check back later for exciting upcoming events!
							</p>
						</div>
					)}
				</div>
			</section>

			{/* Footer */}
			<Footer />
		</Fragment>
	)
}
